import React, { useState, useEffect, useRef } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Loader2, AlertCircle, Save, CheckCircle2 } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import { Input } from "@cms/ui/components/input";
import { Alert, AlertDescription, AlertTitle } from "@cms/ui/components/alert";
import { LocalTabsHeader } from "@cms/ui/components/section-tabs";
import { ColumnBlocksEditor, type ColumnBlocksEditorHandle } from "../components/column-blocks-editor";
import { useMediaPicker } from "../components/media-picker-dialog";
import { PostSettingsPanel, type PostDraft } from "../components/post-settings-panel";
import { SearchListingCard } from "../components/search-listing-card";
import { FaqsCard } from "../components/faqs-card";
import {
  fetchPost,
  fetchPostAuthors,
  fetchPostCategories,
  savePost,
  type PostAuthor,
  type PostCategory,
  type PostDetail,
  type PostFaq,
} from "../lib/posts";
import { postSlugProblem } from "../lib/post-slug";
import type { ClientOutletContext } from "../lib/outlet-context";

type BlockData = Record<string, any>;
type EditorTab = "content" | "seo";

function parsePostBlocks(content: string): BlockData[] {
  if (!content.trim()) return [];
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed?.blocks) ? parsed.blocks : [];
  } catch {
    return [];
  }
}

function parseFaqsField(value: string | null): PostFaq[] {
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function draftFromPost(post: PostDetail): PostDraft {
  return {
    slug: post.slug,
    excerpt: post.excerpt ?? "",
    featuredImage: post.featured_image_key ? { key: post.featured_image_key } : null,
    authorUserId: post.author_user_id,
    categoryIds: post.category_ids ?? [],
    metaTitle: post.meta_title ?? "",
    metaDesc: post.meta_desc ?? "",
    faqs: parseFaqsField(post.faqs),
    published: post.published ? 1 : 0,
    // D1 CURRENT_TIMESTAMP is "YYYY-MM-DD HH:MM:SS"; the date input wants YYYY-MM-DD.
    publishedAt: post.published_at ? post.published_at.slice(0, 10) : "",
  };
}

/**
 * The blog post editor. The Content tab is the writing surface (title + one
 * linear Editor.js document — no sections/columns) beside a settings rail
 * (publish state, listing, author, categories); the SEO & AI tab holds the
 * search listing (with a Google-result preview) and FAQ schema.
 */
export function PostEditorPage() {
  const { id } = useParams();
  const { api, status, refreshStatus, setBreadcrumbs } =
    useOutletContext<ClientOutletContext>();

  const [post, setPost] = useState<PostDetail | null>(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState<EditorTab>("content");
  const [title, setTitle] = useState("");
  const [draft, setDraft] = useState<PostDraft | null>(null);
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [authors, setAuthors] = useState<PostAuthor[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ variant: "success" | "destructive"; text: string } | null>(null);

  const editorRef = useRef<ColumnBlocksEditorHandle | null>(null);
  const { pickImage, pickerElement } = useMediaPicker(api);

  // FAQ schema (AEO) is a Growth/Pro feature, same gate as pages.
  const canEditFaqs = status?.plan === "growth" || status?.plan === "pro";
  const slugError = draft ? postSlugProblem(draft.slug) : "";

  useEffect(() => {
    if (!api || !id) return;
    fetchPost(api, id)
      .then((data) => {
        setPost(data);
        setTitle(data.title);
        setDraft(draftFromPost(data));
      })
      .catch((err: Error) => setLoadError(err.message));
    fetchPostCategories(api).then(setCategories).catch(() => {});
    fetchPostAuthors(api).then(setAuthors).catch(() => {});
  }, [api, id]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    if (!setBreadcrumbs) return undefined;
    setBreadcrumbs(post ? [
      {
        label: "Blog",
        href: "/blog",
        onClick: (event: React.MouseEvent) => {
          if (dirty && !window.confirm("You have unsaved changes. Leave anyway?")) {
            event.preventDefault();
          }
        },
      },
      { label: post.title },
    ] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, post, dirty]);

  const markDirty = () => setDirty(true);
  const updateDraft = (patch: Partial<PostDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    markDirty();
  };

  const save = async () => {
    if (!api || !draft || !id) return;
    if (!title.trim()) {
      setNotice({ variant: "destructive", text: "Give the post a title before saving." });
      return;
    }
    if (slugError) {
      setNotice({ variant: "destructive", text: slugError });
      return;
    }

    setSaving(true);
    setNotice(null);
    try {
      const blocks = editorRef.current
        ? await editorRef.current.getBlocks()
        : parsePostBlocks(post?.content ?? "");

      await savePost(api, id, {
        title: title.trim(),
        ...(draft.slug !== post?.slug && { slug: draft.slug }),
        excerpt: draft.excerpt.trim() || null,
        content: JSON.stringify({ blocks }),
        featured_image_key: draft.featuredImage?.key || null,
        author_user_id: draft.authorUserId,
        category_ids: draft.categoryIds,
        meta_title: draft.metaTitle || null,
        meta_desc: draft.metaDesc || null,
        published: draft.published,
        published_at: draft.publishedAt || null,
        ...(canEditFaqs && { faqs: draft.faqs }),
      });

      setDirty(false);
      refreshStatus?.();
      setNotice({ variant: "success", text: "Saved. Publish the site to make changes go live." });
      setPost((prev) => (prev ? { ...prev, title: title.trim(), slug: draft.slug } : prev));
    } catch (err) {
      setNotice({ variant: "destructive", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load this post</AlertTitle>
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  // A loaded post implies a configured API — this narrows `api` for the JSX.
  if (!api || !post || !draft) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <LocalTabsHeader<EditorTab>
        tabs={[
          { id: "content", label: "Content" },
          { id: "seo", label: "SEO & AI" },
        ]}
        active={tab}
        onSelect={setTab}
        actions={
          <>
            {dirty && <Badge variant="warning">Unsaved changes</Badge>}
            <Button onClick={() => { void save(); }} disabled={saving || !!slugError} size="sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </>
        }
      />

      {notice && (
        <Alert variant={notice.variant === "success" ? "default" : "destructive"} className="mb-4">
          {notice.variant === "success"
            ? <CheckCircle2 className="h-4 w-4" />
            : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{notice.text}</AlertDescription>
        </Alert>
      )}

      {/* The Content tab stays mounted while SEO & AI is open — unmounting the
          Editor.js instance would drop unsaved blocks on every tab switch. */}
      <div className={tab === "content" ? "grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]" : "hidden"}>
        <div className="space-y-4">
          <Input
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTitle(e.target.value); markDirty(); }}
            placeholder="Post title"
            className="h-auto border-none px-0 text-3xl font-semibold shadow-none focus-visible:ring-0"
            aria-label="Post title"
          />
          <ColumnBlocksEditor
            ref={(handle) => { editorRef.current = handle; }}
            blocks={parsePostBlocks(post.content)}
            onChange={markDirty}
            api={api}
            placeholder="Start writing your post…"
            pickImage={pickImage}
            activeModules={status?.active_modules ?? []}
          />
        </div>

        <PostSettingsPanel
          draft={draft}
          onChange={updateDraft}
          categories={categories}
          onCategoriesChanged={setCategories}
          authors={authors}
          api={api}
          slugError={slugError}
        />
      </div>

      {tab === "seo" && (
        <div className="mx-auto max-w-2xl space-y-4">
          <SearchListingCard
            metaTitle={draft.metaTitle}
            metaDesc={draft.metaDesc}
            onMetaTitleChange={(value) => updateDraft({ metaTitle: value })}
            onMetaDescChange={(value) => updateDraft({ metaDesc: value })}
            titleFallback={title || post.title}
            descFallback={draft.excerpt}
            previewUrl={`${status?.site_url ?? "https://www.your-site.co.uk"}/blog/${draft.slug}/`}
          />
          {canEditFaqs && (
            <FaqsCard faqs={draft.faqs} onChange={(faqs) => updateDraft({ faqs })} />
          )}
        </div>
      )}

      {pickerElement}
    </div>
  );
}
